import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { UseMutateFunction } from "react-query";
import { toast } from "react-toastify";

import { FilterTypes } from "../enums/components";
import { parseLocaleStringBasedOnUser } from "../utils/namesManipulation";
import { CustomModal } from "./CustomModal";
import DynamicForm, { FormField } from "./DynamicForm";

export type MutateInputs = {
  data: FormData;
  onSettled: () => void;
  onSuccess: () => void;
  projectId: number | string;
  rowId: number;
};

type FormData = Record<string, unknown>;

type FormItem = FormField & {
  displayOptionsFn?: (formData: FormData) => string[];
  optionsFn?: (formData: FormData) => string[];
};

type ItemPopUpFormProps = {
  formItems: FormItem[];
  initialFormData?: FormData;
  isOpen: boolean;
  isSubmitting?: boolean;
  mode?: "CREATE" | "EDIT";
  mutate: UseMutateFunction<FormData, Error, MutateInputs, unknown>;
  projectId: number | string;
  requiredFields?: string[];
  rowId?: number;
  setIsOpen: (isOpen: boolean) => void;
  title?: string;
};

const ItemPopUpForm: React.FC<ItemPopUpFormProps> = ({
  formItems,
  initialFormData,
  isOpen,
  isSubmitting,
  mode = "EDIT",
  mutate,
  projectId,
  requiredFields,
  rowId,
  setIsOpen,
  title,
}) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState<FormData>({});

  useEffect(() => {
    if (initialFormData) setFormData(initialFormData);
  }, [initialFormData]);

  if ((!initialFormData || !rowId) && mode === "EDIT") return;

  const fieldsWithActions: FormItem[] = formItems.map((field) => ({
    config: formData,
    filterLabel: field?.props?.label,
    filterLabelClassName: "text-lds-foreground-muted font-ds-body-semibold-md w-2/3",
    filterName: field.filterName,
    filterType: field.filterType,
    props: {
      ...field.props,
      displayOptions: field.filterType === FilterTypes.DROPDOWN_SINGLE ? field.displayOptionsFn?.(formData) : [],
      height: "4rem",
      options: field.filterType === FilterTypes.DROPDOWN_SINGLE ? field.optionsFn?.(formData) : [],
      width: "50%",
    },
    required: field.required,
    setConfig: (filterName, newValue) => {
      setFormData({ ...formData, [filterName]: newValue });
    },
    visible: () => true,
  }));

  const handleSubmit = () => {
    const emptyRequiredFields = requiredFields?.filter(
      (f) => formData[f] === undefined || (typeof formData[f] === "string" && formData[f].trim() === "")
    );
    if (emptyRequiredFields && emptyRequiredFields.length > 0) {
      const emptyRequiredFieldsLabel = fieldsWithActions
        .filter((f) => emptyRequiredFields?.includes(f.filterName))
        .map((f) => f.filterLabel);
      toast.error(t("general.form.filledFieldsConstraint", { requiredFields: emptyRequiredFieldsLabel?.join(", ") }));
      return;
    }

    for (const key in formData) {
      const value = formData[key];
      formData[key] = parseLocaleStringBasedOnUser(value);
    }

    mutate({
      data: { ...formData },
      onSettled: () => {
        setIsOpen(false);
        setFormData({});
      },
      onSuccess: () => {
        mode === "CREATE" ? toast.success("Added successfully!") : toast.success("Edited successfully!");
      },
      projectId,
      rowId: rowId || 0,
    });
  };

  return (
    <CustomModal
      className="max-h-[90rem] !pb-4"
      height="95vh"
      modalOpen={isOpen}
      setModalOpen={setIsOpen}
      width="70rem"
    >
      <div className="h-full w-full ">
        <DynamicForm
          fields={fieldsWithActions}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          title={title || ""}
        />
      </div>
    </CustomModal>
  );
};

export default ItemPopUpForm;
